// src/components/admin/sections/AdminHeader.tsx - VERSIÓN COMPLETA MEJORADA
import React, { useState, useEffect } from 'react';
import apiElCristo, { HeaderData, MenuItem } from '../../../service/api_elcristo';
import './AdminHeader.css';

const AdminHeader: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<{ menu: MenuItem, index: number } | null>(null);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'menu' | 'chat'>('general');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [headerData, setHeaderData] = useState<HeaderData>({
    logo: '',
    contactInfo: {
      phone: '',
      email: '',
      address: ''
    },
    socialLinks: {
      facebook: '',
      twitter: '',
      youtube: '',
      linkedin: ''
    },
    menuItems: [],
    ctaButton: {
      text: '',
      url: '',
      image: ''
    },
    chatConfig: {
      enabled: false,
      botName: '',
      welcomeMessage: '',
      position: 'bottom-right',
      primaryColor: '#ff0f00',
      buttonSize: 'medium',
      theme: 'light'
    }
  });

  const [menuFormData, setMenuFormData] = useState<MenuItem>({
    text: '',
    url: '',
    subMenu: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getHeaderData();
      
      const { modalConfig, ...headerOnly } = data as any;
      
      console.log('📥 Header data:', headerOnly);
      setHeaderData(headerOnly);
    } catch (error) {
      console.error('❌ Error loading header:', error);
      setMessage({ type: 'error', text: 'Error al cargar header' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section: string, field?: string) => {
    const { value, type } = e.target;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    if (field) {
      setHeaderData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof HeaderData] as any),
          [field]: finalValue
        }
      }));
    } else {
      setHeaderData(prev => ({
        ...prev,
        [section]: finalValue
      }));
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo PNG, JPG, SVG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ Máximo 2MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingLogo(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'logos');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir');
      }
      
      const data = await response.json();
      
      setHeaderData(prev => ({ ...prev, logo: data.url }));
      
      setMessage({ type: 'success', text: '✅ Logo subido' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: `❌ ${err.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleOpenMenuModal = (item?: MenuItem, index?: number) => {
    if (item && index !== undefined) {
      setEditingMenuItem(item);
      setEditingIndex(index);
      setMenuFormData({ ...item });
    } else {
      setEditingMenuItem(null);
      setEditingIndex(null);
      setMenuFormData({
        text: '',
        url: '',
        subMenu: []
      });
    }
    setShowMenuModal(true);
  };

  const handleCloseMenuModal = () => {
    setShowMenuModal(false);
    setEditingMenuItem(null);
    setEditingIndex(null);
  };

  const handleMenuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMenuFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveMenuItem = async () => {
    if (!menuFormData.text || !menuFormData.url) {
      setMessage({ type: 'error', text: '❌ Completa todos los campos' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSaving(true);

    try {
      let updatedMenu;
      
      if (editingIndex !== null) {
        updatedMenu = [...headerData.menuItems];
        updatedMenu[editingIndex] = menuFormData;
      } else {
        updatedMenu = [...headerData.menuItems, menuFormData];
      }

      const updatedData = { ...headerData, menuItems: updatedMenu };
      
      console.log('📤 Guardando menú:', updatedData);
      
      await apiElCristo.updateSiteContent({ header: updatedData });
      
      setHeaderData(updatedData);
      handleCloseMenuModal();
      
      setMessage({ type: 'success', text: '✅ Menú guardado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (menu: MenuItem, index: number) => {
    setMenuToDelete({ menu, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMenuToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!menuToDelete) return;

    setSaving(true);

    try {
      const updatedMenu = headerData.menuItems.filter((_, i) => i !== menuToDelete.index);
      const updatedData = { ...headerData, menuItems: updatedMenu };
      
      console.log('📤 Eliminando menú:', updatedData);
      
      await apiElCristo.updateSiteContent({ header: updatedData });
      
      setHeaderData(updatedData);
      handleCloseDeleteModal();
      
      setMessage({ type: 'success', text: '✅ Menú eliminado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveMenuItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= headerData.menuItems.length) return;

    const items = [...headerData.menuItems];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setHeaderData(prev => ({ ...prev, menuItems: items }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        logo: headerData.logo || '',
        contactInfo: {
          phone: headerData.contactInfo?.phone || '',
          email: headerData.contactInfo?.email || '',
          address: headerData.contactInfo?.address || ''
        },
        socialLinks: {
          facebook: headerData.socialLinks?.facebook || '',
          twitter: headerData.socialLinks?.twitter || '',
          youtube: headerData.socialLinks?.youtube || '',
          linkedin: headerData.socialLinks?.linkedin || ''
        },
        menuItems: headerData.menuItems.map(item => ({
          text: item.text,
          url: item.url,
          subMenu: item.subMenu || []
        })),
        ctaButton: {
          text: headerData.ctaButton?.text || '',
          url: headerData.ctaButton?.url || '',
          image: headerData.ctaButton?.image || ''
        },
        chatConfig: {
          enabled: Boolean(headerData.chatConfig?.enabled),
          botName: headerData.chatConfig?.botName || '',
          welcomeMessage: headerData.chatConfig?.welcomeMessage || '',
          position: headerData.chatConfig?.position || 'bottom-right',
          primaryColor: headerData.chatConfig?.primaryColor || '#ff0f00',
          buttonSize: headerData.chatConfig?.buttonSize || 'medium',
          theme: headerData.chatConfig?.theme || 'light'
        }
      };

      console.log('📤 Guardando header:', payload);
      
      await apiElCristo.updateSiteContent({ header: payload });
      
      setMessage({ type: 'success', text: '✅ Header actualizado' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      
      let errorMsg = 'Error al guardar';
      if (error.message) errorMsg = error.message;
      if (error.response?.data?.detail) errorMsg = error.response.data.detail;
      
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-header-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-header-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-bars"></i> Header & Navegación</h1>
          <p>Configuración del encabezado y menú</p>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <div className="tabs-container">
        <button
          type="button"
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <i className="fas fa-cog"></i>
          General
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'menu' ? 'active' : ''}`}
          onClick={() => setActiveTab('menu')}
        >
          <i className="fas fa-bars"></i>
          Menú ({headerData.menuItems.length})
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <i className="fas fa-comments"></i>
          Chat
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === 'general' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-image"></i> Logo del Sitio</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>URL del Logo</label>
                  
                  {headerData.logo && (
                    <div className="logo-preview-admin">
                      <img 
                        src={headerData.logo.startsWith('http') 
                          ? headerData.logo 
                          : `${window.location.origin}${headerData.logo}`
                        } 
                        alt="Logo" 
                      />
                    </div>
                  )}
                  
                  <div className="image-upload-group">
                    <input
                      type="text"
                      value={headerData.logo}
                      onChange={(e) => handleChange(e, 'logo')}
                      placeholder="/assets/kidsa/assets/img/logo/logoheader.png"
                      className="form-control"
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload"
                      disabled={uploadingLogo}
                      onClick={() => document.getElementById('logo-input')?.click()}
                    >
                      {uploadingLogo ? (
                        <><div className="spinner-small"></div> Subiendo...</>
                      ) : (
                        <><i className="fas fa-upload"></i> Subir</>
                      )}
                    </button>
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <small className="hint">
                    <i className="fas fa-info-circle"></i> PNG, JPG, SVG, WEBP (máx 2MB)
                  </small>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-address-book"></i> Contacto</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fas fa-phone"></i> Teléfono</label>
                    <input
                      type="text"
                      value={headerData.contactInfo.phone}
                      onChange={(e) => handleChange(e, 'contactInfo', 'phone')}
                      placeholder="+56 9 4696 6778"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label><i className="fas fa-envelope"></i> Email</label>
                    <input
                      type="email"
                      value={headerData.contactInfo.email}
                      onChange={(e) => handleChange(e, 'contactInfo', 'email')}
                      placeholder="contacto@colegio.cl"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-map-marker-alt"></i> Dirección</label>
                  <input
                    type="text"
                    value={headerData.contactInfo.address}
                    onChange={(e) => handleChange(e, 'contactInfo', 'address')}
                    placeholder="Dirección completa"
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-share-alt"></i> Redes Sociales</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fab fa-facebook"></i> Facebook</label>
                    <input
                      type="url"
                      value={headerData.socialLinks.facebook}
                      onChange={(e) => handleChange(e, 'socialLinks', 'facebook')}
                      placeholder="https://facebook.com/..."
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label><i className="fab fa-twitter"></i> Twitter</label>
                    <input
                      type="url"
                      value={headerData.socialLinks.twitter}
                      onChange={(e) => handleChange(e, 'socialLinks', 'twitter')}
                      placeholder="https://twitter.com/..."
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fab fa-youtube"></i> YouTube</label>
                    <input
                      type="url"
                      value={headerData.socialLinks.youtube}
                      onChange={(e) => handleChange(e, 'socialLinks', 'youtube')}
                      placeholder="https://youtube.com/..."
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label><i className="fab fa-linkedin"></i> LinkedIn</label>
                    <input
                      type="url"
                      value={headerData.socialLinks.linkedin}
                      onChange={(e) => handleChange(e, 'socialLinks', 'linkedin')}
                      placeholder="https://linkedin.com/..."
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-mouse-pointer"></i> Botón CTA</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Texto</label>
                    <input
                      type="text"
                      value={headerData.ctaButton.text}
                      onChange={(e) => handleChange(e, 'ctaButton', 'text')}
                      placeholder="Admisión"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>URL</label>
                    <input
                      type="text"
                      value={headerData.ctaButton.url}
                      onChange={(e) => handleChange(e, 'ctaButton', 'url')}
                      placeholder="/admision"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'menu' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-bars"></i> Menú ({headerData.menuItems.length})</h3>
                <button type="button" className="btn btn-primary" onClick={() => handleOpenMenuModal()}>
                  <i className="fas fa-plus"></i>
                  Nuevo Ítem
                </button>
              </div>
              <div className="card-body">
                {headerData.menuItems.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-bars"></i>
                    <p>No hay ítems en el menú</p>
                    <button type="button" className="btn btn-primary" onClick={() => handleOpenMenuModal()}>
                      <i className="fas fa-plus"></i>
                      Crear Ítem
                    </button>
                  </div>
                ) : (
                  <div className="menu-items-list">
                    {headerData.menuItems.map((item, index) => (
                      <div key={index} className="menu-item-card">
                        <div className="menu-item-drag">
                          <i className="fas fa-grip-vertical"></i>
                        </div>
                        
                        <div className="menu-item-content">
                          <h4>{item.text}</h4>
                          <p>{item.url}</p>
                          {item.subMenu && item.subMenu.length > 0 && (
                            <span className="submenu-badge">
                              {item.subMenu.length} submenú(s)
                            </span>
                          )}
                        </div>
                        
                        <div className="menu-item-actions">
                          <button type="button" className="btn-icon" onClick={() => handleMoveMenuItem(index, 'up')} disabled={index === 0}>
                            <i className="fas fa-arrow-up"></i>
                          </button>
                          <button type="button" className="btn-icon" onClick={() => handleMoveMenuItem(index, 'down')} disabled={index === headerData.menuItems.length - 1}>
                            <i className="fas fa-arrow-down"></i>
                          </button>
                          <button type="button" className="btn-icon btn-edit" onClick={() => handleOpenMenuModal(item, index)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button type="button" className="btn-icon btn-delete" onClick={() => handleOpenDeleteModal(item, index)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-comments"></i> Chat Widget</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={headerData.chatConfig?.enabled || false}
                      onChange={(e) => {
                        setHeaderData(prev => ({
                          ...prev,
                          chatConfig: {
                            ...prev.chatConfig!,
                            enabled: e.target.checked
                          }
                        }));
                      }}
                    />
                    <span>Habilitar Chat Widget</span>
                  </label>
                </div>

                {headerData.chatConfig?.enabled && (
                  <>
                    <div className="form-group">
                      <label>Nombre del Bot</label>
                      <input
                        type="text"
                        value={headerData.chatConfig?.botName || ''}
                        onChange={(e) => handleChange(e, 'chatConfig', 'botName')}
                        placeholder="Asistente Virtual"
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label>Mensaje Bienvenida</label>
                      <input
                        type="text"
                        value={headerData.chatConfig?.welcomeMessage || ''}
                        onChange={(e) => handleChange(e, 'chatConfig', 'welcomeMessage')}
                        placeholder="¡Hola! ¿En qué puedo ayudarte?"
                        className="form-control"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Posición</label>
                        <select value={headerData.chatConfig?.position || 'bottom-right'} onChange={(e) => handleChange(e, 'chatConfig', 'position')} className="form-control">
                          <option value="bottom-right">Abajo Derecha</option>
                          <option value="bottom-left">Abajo Izquierda</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Tamaño</label>
                        <select value={headerData.chatConfig?.buttonSize || 'medium'} onChange={(e) => handleChange(e, 'chatConfig', 'buttonSize')} className="form-control">
                          <option value="small">Pequeño</option>
                          <option value="medium">Mediano</option>
                          <option value="large">Grande</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Color Principal</label>
                        <input
                          type="color"
                          value={headerData.chatConfig?.primaryColor || '#ff0f00'}
                          onChange={(e) => handleChange(e, 'chatConfig', 'primaryColor')}
                          className="form-control"
                          style={{ height: '45px' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Tema</label>
                        <select value={headerData.chatConfig?.theme || 'light'} onChange={(e) => handleChange(e, 'chatConfig', 'theme')} className="form-control">
                          <option value="light">Claro</option>
                          <option value="dark">Oscuro</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i> Recargar
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving || uploadingLogo}>
            {saving ? (
              <><div className="spinner-small"></div> Guardando...</>
            ) : (
              <><i className="fas fa-save"></i> Guardar Cambios</>
            )}
          </button>
        </div>
      </form>

      {/* MODAL CREAR/EDITAR MENU */}
      {showMenuModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '600px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-bars"></i>
                {editingMenuItem ? 'Editar Ítem' : 'Nuevo Ítem'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseMenuModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <div className="form-group">
                <label>Texto del Menú *</label>
                <input
                  type="text"
                  name="text"
                  value={menuFormData.text}
                  onChange={handleMenuChange}
                  placeholder="Inicio, Nosotros, Contacto"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>URL *</label>
                <input
                  type="text"
                  name="url"
                  value={menuFormData.url}
                  onChange={handleMenuChange}
                  placeholder="./ o ./#nosotros"
                  className="form-control"
                />
              </div>

              <div className="info-box">
                <i className="fas fa-info-circle"></i>
                <p>Los submenús se configuran en el código. Este editor permite ítems principales.</p>
              </div>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseMenuModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveMenuItem} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Guardando...</>
                ) : (
                  <>{editingMenuItem ? 'Actualizar' : 'Agregar'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW SECTION */}
      <div className="header-preview-section">
        <div className="header-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa del Header</h3>
        </div>
        
        <div className="header-preview">
          {/* Top Bar */}
          <div className="preview-top-bar">
            <div className="preview-contact-info">
              {headerData.contactInfo.phone && (
                <div className="preview-contact-item">
                  <i className="fas fa-phone"></i>
                  <span>{headerData.contactInfo.phone}</span>
                </div>
              )}
              {headerData.contactInfo.email && (
                <div className="preview-contact-item">
                  <i className="fas fa-envelope"></i>
                  <span>{headerData.contactInfo.email}</span>
                </div>
              )}
              {headerData.contactInfo.address && (
                <div className="preview-contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{headerData.contactInfo.address}</span>
                </div>
              )}
            </div>
            
            <div className="preview-social-links">
              {headerData.socialLinks.facebook && (
                <a href={headerData.socialLinks.facebook} className="preview-social-icon">
                  <i className="fab fa-facebook"></i>
                </a>
              )}
              {headerData.socialLinks.twitter && (
                <a href={headerData.socialLinks.twitter} className="preview-social-icon">
                  <i className="fab fa-twitter"></i>
                </a>
              )}
              {headerData.socialLinks.youtube && (
                <a href={headerData.socialLinks.youtube} className="preview-social-icon">
                  <i className="fab fa-youtube"></i>
                </a>
              )}
              {headerData.socialLinks.linkedin && (
                <a href={headerData.socialLinks.linkedin} className="preview-social-icon">
                  <i className="fab fa-linkedin"></i>
                </a>
              )}
            </div>
          </div>

          {/* Main Header */}
          <div className="preview-main-header">
            <div className="preview-logo">
              {headerData.logo ? (
                <img 
                  src={headerData.logo.startsWith('http') 
                    ? headerData.logo 
                    : `${window.location.origin}${headerData.logo}`
                  } 
                  alt="Logo" 
                />
              ) : (
                <div className="preview-logo-placeholder">LOGO</div>
              )}
            </div>

            <nav className="preview-menu">
              {headerData.menuItems.length > 0 ? (
                headerData.menuItems.map((item, index) => (
                  <a key={index} href={item.url} className="preview-menu-item">
                    {item.text}
                    {item.subMenu && item.subMenu.length > 0 && (
                      <i className="fas fa-chevron-down"></i>
                    )}
                  </a>
                ))
              ) : (
                <>
                  <a href="#" className="preview-menu-item">Inicio</a>
                  <a href="#" className="preview-menu-item">Nosotros</a>
                  <a href="#" className="preview-menu-item">Programas</a>
                  <a href="#" className="preview-menu-item">Contacto</a>
                </>
              )}
            </nav>

            {headerData.ctaButton.text && (
              <a href={headerData.ctaButton.url} className="preview-cta-button">
                {headerData.ctaButton.text}
                <i className="fas fa-arrow-right"></i>
              </a>
            )}

            <button className="preview-mobile-toggle">
              <i className="fas fa-bars"></i>
            </button>
          </div>

          {/* Chat Widget Preview */}
          {headerData.chatConfig?.enabled && (
            <div className="preview-chat-widget" style={{
              backgroundColor: headerData.chatConfig.primaryColor,
              right: headerData.chatConfig.position === 'bottom-right' ? '20px' : 'auto',
              left: headerData.chatConfig.position === 'bottom-left' ? '20px' : 'auto',
              width: headerData.chatConfig.buttonSize === 'small' ? '50px' : 
                     headerData.chatConfig.buttonSize === 'large' ? '70px' : '60px',
              height: headerData.chatConfig.buttonSize === 'small' ? '50px' : 
                      headerData.chatConfig.buttonSize === 'large' ? '70px' : '60px'
            }}>
              <i className="fas fa-comments"></i>
            </div>
          )}
        </div>
      </div>

      {/* MODAL DELETE */}
      {showDeleteModal && menuToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar ítem de menú <strong>"{menuToDelete.menu.text}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                No se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHeader;